import React, { useState, useEffect } from "react";
import { RefreshCw, X, Save, Edit2, Eye, EyeOff } from "lucide-react";
import { format } from "date-fns";
import Alert from "./Alert";

export const OrderDetailsModal = ({
  order: initialOrder,
  onClose,
  onUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [order, setOrder] = useState(initialOrder);
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    // Mark order as viewed when opened if not already viewed
    if (!order.isViewed) {
      handleViewedStatus(true);
    }
  }, []);

  if (!order) return null;

  const addAlert = (type, message) => {
    const id = Date.now();
    setAlerts((prev) => [...prev, { id, type, message }]);
    if (type === "success") {
      setTimeout(() => removeAlert(id), 3000);
    }
  };

  const removeAlert = (id) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setAlerts([]);
  };

  const handleInputChange = (section, field, value) => {
    setOrder((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleViewedStatus = async (viewed) => {
    try {
      setLoading(true);

      // Update viewed status in the API
      const response = await fetch(`https://api.elza2.store/api/v1/order/${order._id}/viewed`, {
        method: "PATCH",
        headers: {
          "Authorization": localStorage.getItem("Auth"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ isViewed: viewed }),
      });

      if (!response.ok) throw new Error("Failed to update viewed status");

      setOrder((prev) => ({
        ...prev,
        isViewed: viewed,
      }));

      addAlert(
        "success",
        viewed ? "Order marked as viewed" : "Order marked as unviewed"
      );
    } catch (err) {
      addAlert("error", err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (field, value) => {
    setOrder((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setAlerts([]);

      const response = await fetch(`https://api.elza2.store/api/v1/order/${order._id}`, {
        method: "PUT",
        headers: {
          "Authorization": localStorage.getItem("Auth"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(order),
      });

      if (!response.ok) throw new Error("Failed to update order");

      addAlert("success", "Order updated successfully!");
      onUpdate?.(order);
      setTimeout(() => setIsEditing(false), 1500);
    } catch (err) {
      addAlert("error", err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (isActive) =>
    isActive ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-full max-w-4xl mx-4 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-800">
              Order #{order._id.slice(-6)}
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleViewedStatus(!order.isViewed)}
                className={`p-2 rounded-lg flex items-center gap-2 ${
                  order.isViewed
                    ? "bg-green-50 text-green-600 hover:bg-green-100"
                    : "bg-yellow-50 text-yellow-600 hover:bg-yellow-100"
                } transition-colors`}
                title={order.isViewed ? "Mark as unviewed" : "Mark as viewed"}
              >
                {order.isViewed ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
              <button
                onClick={handleEditToggle}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  isEditing
                    ? "bg-gray-100 text-gray-600"
                    : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                } transition-colors`}
              >
                <Edit2 size={18} />
                {isEditing ? "Cancel Edit" : "Edit Order"}
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 transition-colors p-2 rounded-lg hover:bg-gray-100"
          >
            <X size={24} />
          </button>
        </div>

        {alerts.length > 0 && (
          <div className="p-6 pt-0 space-y-2">
            {alerts.map((alert) => (
              <Alert
                key={alert.id}
                type={alert.type}
                message={alert.message}
                onClose={() => removeAlert(alert.id)}
              />
            ))}
          </div>
        )}

        {/* Modal Content */}
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Shipping Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                Shipping Details
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                {[
                  "firstName",
                  "lastName",
                  "phone",
                  "streetAddress",
                  "city",
                  "zipCode",
                  "notes",
                ].map((field) => (
                  <div key={field} className="space-y-1">
                    <label className="text-sm font-medium text-gray-600 capitalize">
                      {field.replace(/([A-Z])/g, " $1").trim()}
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={order.ShippingAddress?.[field] || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "ShippingAddress",
                            field,
                            e.target.value
                          )
                        }
                        className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      />
                    ) : (
                      <p className="text-gray-800">
                        {order.ShippingAddress?.[field] || "-"}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Order Status */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-700">
                Order Status
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">
                    Phase
                  </label>
                  {isEditing ? (
                    <select
                      value={order.phase}
                      onChange={(e) =>
                        handleStatusChange("phase", e.target.value)
                      }
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    >
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="shipping">Shipping</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  ) : (
                    <span className="capitalize px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {order.phase}
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  <span className="text-sm font-medium text-gray-600">
                    Status
                  </span>
                  <div className="flex flex-wrap gap-4">
                    {isEditing ? (
                      <>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={order.isPaid}
                            onChange={(e) =>
                              handleStatusChange("isPaid", e.target.checked)
                            }
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span>Paid</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={order.isDelevered}
                            onChange={(e) =>
                              handleStatusChange(
                                "isDelevered",
                                e.target.checked
                              )
                            }
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span>Delivered</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={order.isViewed}
                            onChange={(e) =>
                              handleViewedStatus(e.target.checked)
                            }
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span>Viewed</span>
                        </label>
                      </>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                            order.isPaid
                          )}`}
                        >
                          {order.isPaid ? "Paid" : "Unpaid"}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                            order.isDelevered
                          )}`}
                        >
                          {order.isDelevered ? "Delivered" : "Not Delivered"}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                            order.isViewed
                          )}`}
                        >
                          {order.isViewed ? "Viewed" : "Not Viewed"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-sm font-medium text-gray-600">
                    Order Date
                  </span>
                  <p className="text-gray-800">
                    {format(new Date(order.createdAt), "MMMM dd, yyyy")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Products Table */}
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Order Items</h3>
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="p-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {order.orderItems.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="p-3 text-sm text-gray-700">
                        {item.product?.title || "Custom Product"}
                      </td>
                      <td className="p-3 text-right text-sm text-gray-700">
                        {item.quantity}
                      </td>
                      <td className="p-3 text-right text-sm text-gray-700">
                        EGP {(item.price * item.quantity).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td
                      colSpan="2"
                      className="p-3 text-right font-medium text-gray-700"
                    >
                      Subtotal:
                    </td>
                    <td className="p-3 text-right text-sm font-bold text-gray-800">
                      EGP {order.subtotal?.toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td
                      colSpan="2"
                      className="p-3 text-right font-medium text-gray-700"
                    >
                      Shipping:
                    </td>
                    <td className="p-3 text-right text-sm text-gray-800">
                      EGP {order.shippingCost?.toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td
                      colSpan="2"
                      className="p-3 text-right font-bold text-gray-900"
                    >
                      Total:
                    </td>
                    <td className="p-3 text-right text-lg font-bold text-blue-600">
                      EGP {order.totalPrice?.toLocaleString()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Save Button */}
          {isEditing && (
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSave}
                disabled={loading}
                className={`
                  px-6 py-2 rounded-lg flex items-center gap-2 text-white
                  ${
                    loading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }
                  transition-colors
                `}
              >
                {loading ? (
                  <RefreshCw className="animate-spin" size={18} />
                ) : (
                  <Save size={18} />
                )}
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
